/**
 * features/settings/BackupTab.tsx — สำรอง/กู้คืนข้อมูล (admin only)
 *
 * 1. ดาวน์โหลดและกู้คืนไฟล์ JSON ของระบบ
 * 2. สำรองข้อมูลขึ้น Google Drive ผ่าน Google OAuth 2.0 (Gmail ส่วนตัว) หรือ Service Account
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Cloud,
  Copy,
  Download,
  Folder,
  Globe,
  KeyRound,
  LogIn,
  RefreshCw,
  Save,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, resolveAbsoluteApiUrl } from '~/lib/api';
import { queryKeys } from '~/lib/query-client';
import type { GoogleDriveBackupConfig } from '~/lib/types';
import { ConfirmDialog, useToast, WarningBanner } from '~/ui/overlays';
import { Alert, Button, Field, Input } from '~/ui/primitives';

export function BackupTab() {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Local JSON Backup / Restore
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [pendingFile, setPendingFile] = useState<Record<string, unknown> | null>(null);

  // Google Drive Settings State
  const [showConfigForm, setShowConfigForm] = useState(false);

  // OAuth 2.0 Form State
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [connectingOAuth, setConnectingOAuth] = useState(false);

  // Common Form State
  const [folderId, setFolderId] = useState('');
  const [retentionDays, setRetentionDays] = useState(30);
  const [autoSchedule, setAutoSchedule] = useState(true);
  const [enabled, setEnabled] = useState(true);

  // Load Google Drive Config
  const { data: gdriveConfig, isLoading: loadingConfig } = useQuery({
    queryKey: queryKeys.googleDriveBackup,
    queryFn: () => api.get<GoogleDriveBackupConfig>('/api/backup/google-drive'),
  });

  // ฟัง event เมื่อ popup ของ Google OAuth ทำงานเสร็จ
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type === 'GOOGLE_DRIVE_AUTH_SUCCESS') {
        const email = event.data.email || '';
        toast.success(`เชื่อมต่อ Google Drive สำเร็จ: เข้าสู่ระบบด้วย ${email}`);
        queryClient.invalidateQueries({ queryKey: queryKeys.googleDriveBackup });
        setShowConfigForm(false);
        setConnectingOAuth(false);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient, toast]);

  // Test Connection Mutation
  const testMutation = useMutation({
    mutationFn: (body: {
      clientId?: string;
      clientSecret?: string;
      refreshToken?: string;
      folderId?: string;
    }) =>
      api.post<{ success: boolean; folderName: string; testFileName: string }>(
        '/api/backup/google-drive/test',
        body,
      ),
    onSuccess: (data) => {
      toast.success(
        `ทดสอบสำเร็จ: เขียนไฟล์ "${data.testFileName}" ลงในโฟลเดอร์ "${data.folderName}" บน Google Drive แล้ว`,
      );
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'ทดสอบการเชื่อมต่อไม่สำเร็จ');
    },
  });

  // Save Config Mutation
  const saveConfigMutation = useMutation({
    mutationFn: (body: {
      folderId?: string;
      retentionDays?: number;
      autoSchedule?: boolean;
      enabled?: boolean;
    }) => api.post<GoogleDriveBackupConfig>('/api/backup/google-drive/config', body),
    onSuccess: (newConfig) => {
      queryClient.setQueryData(queryKeys.googleDriveBackup, newConfig);
      toast.success('บันทึกการตั้งค่า Google Drive สำเร็จ');
      setShowConfigForm(false);
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'บันทึกการตั้งค่าไม่สำเร็จ');
    },
  });

  // Manual Backup Now Mutation
  const backupNowMutation = useMutation({
    mutationFn: () =>
      api.post<{ success: boolean; fileName: string; deletedOldFiles: number }>(
        '/api/backup/google-drive/upload-now',
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.googleDriveBackup });
      toast.success(`สำรองข้อมูลขึ้น Google Drive สำเร็จ: ${res.fileName}`);
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'สำรองข้อมูลขึ้น Drive ล้มเหลว');
    },
  });

  // เริ่มต้น OAuth Flow ด้วย Popup
  async function handleStartOAuth() {
    if (!clientId.trim()) {
      toast.error('กรุณาระบุ Client ID จาก Google Cloud Console');
      return;
    }
    if (!clientSecret.trim()) {
      toast.error('กรุณาระบุ Client Secret');
      return;
    }
    if (!folderId.trim() && !gdriveConfig?.folderId) {
      toast.error('กรุณาระบุ Folder ID หรือ URL โฟลเดอร์เป้าหมายบน Google Drive');
      return;
    }

    setConnectingOAuth(true);
    try {
      const redirectUri = resolveAbsoluteApiUrl('/api/backup/google-drive/oauth/callback');
      const res = await api.post<{ url: string }>('/api/backup/google-drive/oauth/url', {
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        redirectUri,
        folderId: folderId.trim() || undefined,
      });

      // เปิดหน้าจอยินยอมของ Google ในหน้าต่าง Popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        res.url,
        'google-drive-oauth',
        `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`,
      );

      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        toast.error('เบราว์เซอร์บล็อกหน้าต่าง Popup กรุณากดยินยอมให้เปิด Popup ในเบราว์เซอร์ของคุณ');
        setConnectingOAuth(false);
        return;
      }

      // ตรวจสอบว่า Popup ถูกปิดด้วยตนเองหรือไม่
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          setConnectingOAuth(false);
        }
      }, 1000);
    } catch (error) {
      setConnectingOAuth(false);
      toast.error(error instanceof ApiError ? error.message : 'สร้างลิงก์ล็อกอินไม่สำเร็จ');
    }
  }

  // แลกเปลี่ยน Auth Code แบบแมนนวล (กรณีไม่ใช้ Popup)
  async function handleManualExchange() {
    if (!authCode.trim() || !clientId.trim() || !clientSecret.trim()) {
      toast.error('กรุณาระบุ Authorization Code, Client ID และ Client Secret');
      return;
    }

    setConnectingOAuth(true);
    try {
      const redirectUri = resolveAbsoluteApiUrl('/api/backup/google-drive/oauth/callback');
      const res = await api.post<{ email: string }>('/api/backup/google-drive/oauth/exchange', {
        code: authCode.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        redirectUri,
        folderId: folderId.trim() || undefined,
      });

      toast.success(`เชื่อมต่อ Google Drive สำเร็จ: ${res.email}`);
      queryClient.invalidateQueries({ queryKey: queryKeys.googleDriveBackup });
      setShowConfigForm(false);
      setAuthCode('');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'แลกเปลี่ยน Code ไม่สำเร็จ');
    } finally {
      setConnectingOAuth(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const backup = await api.get<Record<string, unknown>>('/api/backup');
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ft-account-backup-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('ดาวน์โหลดไฟล์สำรองข้อมูลแล้ว');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'สำรองข้อมูลไม่สำเร็จ');
    } finally {
      setExporting(false);
    }
  }

  function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        setPendingFile(parsed);
      } catch {
        toast.error('ไฟล์ที่เลือกไม่ใช่ไฟล์สำรองข้อมูลที่ถูกต้อง');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  }

  async function handleConfirmRestore() {
    if (!pendingFile) return;
    setRestoring(true);
    try {
      await api.post('/api/restore', { ...pendingFile, confirmOverwrite: true });
      toast.success('กู้คืนข้อมูลสำเร็จ กรุณารีเฟรชหน้าเว็บ');
      setPendingFile(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'กู้คืนข้อมูลไม่สำเร็จ');
    } finally {
      setRestoring(false);
    }
  }

  function openEditForm() {
    if (gdriveConfig) {
      setFolderId(gdriveConfig.folderId || '');
      setRetentionDays(gdriveConfig.retentionDays || 30);
      setAutoSchedule(gdriveConfig.autoSchedule);
      setEnabled(gdriveConfig.enabled);
      if (gdriveConfig.clientId) {
        setClientId(gdriveConfig.clientId);
      }
    }
    setShowConfigForm(true);
  }

  function handleTest() {
    testMutation.mutate({
      folderId: folderId || gdriveConfig?.folderId || undefined,
    });
  }

  function handleSaveCommonConfig(e: React.FormEvent) {
    e.preventDefault();
    if (enabled && !folderId.trim() && !gdriveConfig?.folderId) {
      toast.error('กรุณาระบุ Folder ID ก่อนเปิดใช้งาน');
      return;
    }
    saveConfigMutation.mutate({
      folderId: folderId.trim() || undefined,
      retentionDays: Number(retentionDays) || 30,
      autoSchedule,
      enabled: gdriveConfig?.isConfigured ? enabled : false,
    });
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    toast.success('คัดลอกลงคลิปบอร์ดแล้ว');
  }

  const redirectUriDisplay = resolveAbsoluteApiUrl('/api/backup/google-drive/oauth/callback');

  return (
    <div className="max-w-2xl space-y-6">
      {/* ==================== Google Drive Backup ==================== */}
      <div className="card space-y-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
              <Cloud size={22} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-fg">Google Drive Backup</h3>
              <p className="text-xs text-fg-muted">
                สำรองข้อมูลอัตโนมัติขึ้น Google Drive (รองรับ Gmail ส่วนตัว @gmail.com)
              </p>
            </div>
          </div>

          {!loadingConfig && (
            <div>
              {gdriveConfig?.isConfigured ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 size={13} />
                  {gdriveConfig.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งานชั่วคราว'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                  <AlertCircle size={13} />
                  ยังไม่ได้ตั้งค่า
                </span>
              )}
            </div>
          )}
        </div>

        {/* ข้อมูลสถานะการเชื่อมต่อ */}
        {gdriveConfig?.isConfigured && (
          <div className="rounded-lg border border-border-base bg-surface p-3.5 text-xs space-y-2">
            <div className="flex items-center justify-between text-fg-muted">
              <span>บัญชี Google ที่เชื่อมต่อ:</span>
              <div className="flex items-center gap-1 font-mono font-medium text-fg">
                <span>{gdriveConfig.clientEmail || 'Google Account'}</span>
                <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] text-accent-fg">
                  OAuth 2.0
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between text-fg-muted">
              <span>โฟลเดอร์เป้าหมาย (Folder ID):</span>
              <span className="font-mono text-fg">{gdriveConfig.folderId}</span>
            </div>

            <div className="flex items-center justify-between text-fg-muted">
              <span>การสำรองอัตโนมัติ:</span>
              <span className="text-fg">
                {gdriveConfig.autoSchedule ? 'ทำงานทุกวัน เวลา 02:00 น.' : 'ปิด'} (เก็บ {gdriveConfig.retentionDays} วัน)
              </span>
            </div>

            <div className="border-t border-border-base pt-2 flex items-center justify-between">
              <span className="text-fg-muted">สำรองข้อมูลล่าสุด:</span>
              <span className="font-medium text-fg">
                {gdriveConfig.lastBackupAt
                  ? new Date(gdriveConfig.lastBackupAt).toLocaleString('th-TH')
                  : 'ยังไม่มีประวัติการสำรอง'}
                {gdriveConfig.lastStatus === 'success' && (
                  <span className="ml-1.5 text-emerald-600 dark:text-emerald-400 font-semibold">(สำเร็จ)</span>
                )}
                {gdriveConfig.lastStatus === 'failed' && (
                  <span className="ml-1.5 text-rose-600 dark:text-rose-400 font-semibold">(ล้มเหลว)</span>
                )}
              </span>
            </div>

            {gdriveConfig.lastError && (
              <Alert tone="danger">
                ข้อผิดพลาดล่าสุด: {gdriveConfig.lastError}
              </Alert>
            )}
          </div>
        )}

        {/* ปุ่มควบคุม */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {gdriveConfig?.isConfigured && (
            <>
              <Button
                variant="primary"
                onClick={() => backupNowMutation.mutate()}
                loading={backupNowMutation.isPending}
                className="gap-1.5"
              >
                <RefreshCw size={14} />
                สำรองข้อมูลขึ้น Drive ทันที
              </Button>

              <Button
                variant="secondary"
                onClick={handleTest}
                loading={testMutation.isPending}
                className="gap-1.5"
              >
                <Upload size={14} />
                ทดสอบการเขียนไฟล์ (Test File)
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            onClick={() => (showConfigForm ? setShowConfigForm(false) : openEditForm())}
            className="gap-1.5 text-xs"
          >
            <KeyRound size={14} />
            {showConfigForm
              ? 'ซ่อนฟอร์มตั้งค่า'
              : gdriveConfig?.isConfigured
                ? 'แก้ไข / เปลี่ยนการตั้งค่า'
                : 'ตั้งค่าการเชื่อมต่อ Google Drive'}
          </Button>
        </div>

        {/* ฟอร์มตั้งค่า Google Drive */}
        {showConfigForm && (
          <div className="rounded-lg border border-border-base p-4 space-y-4 bg-surface/50 mt-3">
            {/* ==================== OAuth 2.0 Settings ==================== */}
            <div className="space-y-4">
              <div className="rounded border border-blue-200 bg-blue-50/70 p-3 text-xs text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200 space-y-2">
                <p className="font-semibold">ขั้นตอนเชื่อมต่อ Google Drive ด้วย OAuth 2.0:</p>
                <ol className="list-decimal pl-4 space-y-1.5">
                  <li>
                    ไปที่ <strong>Google Cloud Console</strong> &gt; <strong>APIs & Services</strong> &gt; <strong>Credentials</strong>
                  </li>
                  <li>
                    กด <strong>+ CREATE CREDENTIALS</strong> &gt; เลือก <strong>OAuth client ID</strong> (เลือกประเภทเป็น <em>Web application</em>)
                  </li>
                  <li>
                    ในช่อง <strong>Authorized redirect URIs</strong> นำ URL นี้ไปวาง:
                    <div className="mt-1 flex items-center gap-1">
                      <code className="rounded bg-black/10 dark:bg-white/10 px-2 py-0.5 font-mono text-[11px] text-fg select-all">
                        {redirectUriDisplay}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(redirectUriDisplay)}
                        className="rounded p-1 hover:bg-surface-hover text-blue-700 dark:text-blue-300"
                        title="คัดลอก URL"
                      >
                        <Copy size={13} />
                      </button>
                    </div>
                  </li>
                  <li>
                    นำ <strong>Client ID</strong> และ <strong>Client Secret</strong> มาวางในช่องด้านล่าง แล้วกด <em>"เชื่อมต่อด้วยบัญชี Google"</em>
                  </li>
                </ol>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <Field label="Client ID" hint="เช่น 123456789-xxxxxxxx.apps.googleusercontent.com">
                  <Input
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="วาง Client ID จาก Google Cloud Console"
                  />
                </Field>

                <Field label="Client Secret" hint="รหัสลับของ OAuth Client">
                  <Input
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="วาง Client Secret"
                  />
                </Field>
              </div>

              <Field
                label="Google Drive Folder ID (โฟลเดอร์เป้าหมาย)"
                hint="รหัสโฟลเดอร์จาก URL เช่น drive.google.com/drive/folders/1Zc-u98... (วาง URL เต็มได้เลย)"
              >
                <div className="relative">
                  <Folder size={15} className="absolute left-3 top-2.5 text-fg-muted" />
                  <Input
                    value={folderId}
                    onChange={(e) => setFolderId(e.target.value)}
                    placeholder="วาง Folder ID หรือ URL โฟลเดอร์ Account-backup ของคุณ"
                    className="pl-9"
                  />
                </div>
              </Field>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleStartOAuth}
                  loading={connectingOAuth}
                  className="gap-1.5"
                >
                  <LogIn size={15} />
                  เชื่อมต่อด้วยบัญชี Google{gdriveConfig?.clientEmail ? ` (${gdriveConfig.clientEmail})` : ''}
                </Button>
              </div>

              {/* ทางเลือก: ใส่ Auth Code ด้วยตนเอง */}
              <details className="text-xs text-fg-muted pt-2 border-t border-border-base">
                <summary className="cursor-pointer hover:text-fg">
                  หรือต้องการแลกเปลี่ยน Authorization Code ด้วยตนเอง?
                </summary>
                <div className="mt-2 space-y-2">
                  <Input
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    placeholder="วาง Authorization Code ที่ได้รับจาก Google"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleManualExchange}
                    loading={connectingOAuth}
                  >
                    ยืนยัน Code
                  </Button>
                </div>
              </details>
            </div>

            {/* ==================== การตั้งค่ารอบและตัวเลือก ==================== */}
            <form onSubmit={handleSaveCommonConfig} className="space-y-4 pt-3 border-t border-border-base">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="ระยะเวลาเก็บไฟล์สำรอง (Retention)" hint="ลบไฟล์เก่ากว่านี้ออกจาก Drive อัตโนมัติ">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={retentionDays}
                      onChange={(e) => setRetentionDays(Number(e.target.value))}
                      className="w-24"
                    />
                    <span className="text-xs text-fg-muted">วัน</span>
                  </div>
                </Field>

                <Field label="สถานะและรอบการทำงาน">
                  <div className="space-y-2 pt-1">
                    <label className="flex items-center gap-2 text-xs text-fg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        className="rounded border-border-base text-accent focus:ring-accent"
                      />
                      <span>เปิดใช้งานระบบสำรองข้อมูลขึ้น Drive</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-fg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoSchedule}
                        onChange={(e) => setAutoSchedule(e.target.checked)}
                        className="rounded border-border-base text-accent focus:ring-accent"
                      />
                      <span>สำรองข้อมูลอัตโนมัติทุกวัน (02:00 น.)</span>
                    </label>
                  </div>
                </Field>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border-base">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleTest}
                  loading={testMutation.isPending}
                  disabled={!folderId && !gdriveConfig?.folderId}
                  className="text-xs gap-1.5"
                >
                  <RefreshCw size={13} />
                  ทดสอบการเขียนไฟล์
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowConfigForm(false)}
                    className="text-xs"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={saveConfigMutation.isPending}
                    className="text-xs gap-1.5"
                  >
                    <Save size={13} />
                    บันทึกการตั้งค่า
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* ==================== Local File Backup / Restore ==================== */}
      <div className="card p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-fg">สำรองข้อมูลเป็นไฟล์ (Local JSON)</h3>
          <p className="text-xs text-fg-muted">
            ดาวน์โหลดข้อมูลทั้งหมด (ลูกค้า, ผู้ขาย, สินค้า, เอกสาร, ค่าใช้จ่าย) เป็นไฟล์ JSON เก็บไว้ในเครื่อง
            (ไม่รวมรหัสผ่านผู้ใช้)
          </p>
        </div>

        <Button onClick={() => void handleExport()} loading={exporting} className="gap-1.5">
          <Download size={15} />
          ดาวน์โหลดไฟล์สำรองข้อมูล
        </Button>
      </div>

      <div className="card p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-fg">กู้คืนข้อมูลจากไฟล์</h3>
          <p className="text-xs text-fg-muted">
            นำเข้าไฟล์สำรองข้อมูล JSON ที่เคยดาวน์โหลดไว้กลับเข้าระบบ
          </p>
        </div>

        <WarningBanner>
          การกู้คืนจะ<strong>ลบข้อมูลปัจจุบันทั้งหมด</strong>แล้วแทนที่ด้วยข้อมูลในไฟล์ที่เลือก
          การทำรายการนี้ย้อนกลับไม่ได้
        </WarningBanner>

        <Button variant="secondary" onClick={() => fileInputRef.current?.click()} className="gap-1.5">
          <Upload size={15} />
          เลือกไฟล์สำรองข้อมูลเพื่อกู้คืน
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={handleFileSelected}
        />
      </div>

      <ConfirmDialog
        open={Boolean(pendingFile)}
        onClose={() => setPendingFile(null)}
        onConfirm={() => void handleConfirmRestore()}
        title="ยืนยันการกู้คืนข้อมูล"
        message="ข้อมูลปัจจุบันทั้งหมดในระบบจะถูกลบและแทนที่ด้วยข้อมูลจากไฟล์นี้ทันที การทำรายการนี้ย้อนกลับไม่ได้ ยืนยันหรือไม่?"
        confirmLabel="ยืนยันกู้คืนข้อมูล"
        loading={restoring}
      />
    </div>
  );
}
